import { generatePropertyShareText } from "./generatePropertyShareText";
import { PropertyData } from "./propertyCapture";

export interface ShareOptionsCommonConfigs {
  property: PropertyData;
  propertyUrl?: string;
  brochureImageUrl?: string;
  file?: File;
}

export interface ShareViaResult {
  success: boolean;
  error?: string;
}

export const shareVia = async (
  options: ShareOptionsCommonConfigs
): Promise<ShareViaResult> => {
  if (!navigator.share) {
    return {
      success: false,
      error: "Web Share API not supported",
    };
  }

  const { property, propertyUrl, brochureImageUrl, file } = options;
  const finalUrl = propertyUrl || brochureImageUrl;

  const text = generatePropertyShareText(property, {
    includeLink: finalUrl,
  });

  // Helper to create share data
  const createShareData = (includeFile: boolean): ShareData => ({
    title: property.title,
    text,
    ...(finalUrl && { url: finalUrl }),
    ...(includeFile && file && { files: [file] }),
  });

  // Helper to perform share
  const performShare = async (shareData: ShareData): Promise<void> => {
    await navigator.share(shareData);
  };

  try {
    // Try file sharing first if file is provided and supported
    if (file) {
      // Check if file sharing is supported
      const canShareFiles = navigator.canShare
        ? navigator.canShare({ files: [file] })
        : false;

      if (canShareFiles) {
        try {
          await performShare(createShareData(true));
          return { success: true };
        } catch (error: any) {
          // File sharing failed, fall through to URL sharing
          console.warn("File sharing failed, falling back to URL:", error);
        }
      }
    }

    // Fall back to URL/text only sharing
    const shareData = createShareData(false);
    await performShare(shareData);
    return { success: true };
  } catch (error: any) {
    const message = error?.message || "Share failed";

    if (
      message.toLowerCase().includes("abort") ||
      message.toLowerCase().includes("cancel")
    ) {
      return {
        success: false,
        error: "User cancelled sharing",
      };
    }

    return {
      success: false,
      error: message,
    };
  }
};
